import type { PatientWorkspaceSummaryResponseV1 } from "@amplecare/contracts";

import {
  resolveLegacyCompactLeadText,
  resolveLegacyPreviewFallbackPrimaryText,
  resolveLegacyPreviewFallbackSecondaryText,
} from "../shell-core.js";
import { resolvePatientBrand } from "./brand.js";

const brand = resolvePatientBrand();

function resolveDocumentBadgeText(labelText: string): string {
  const normalized = labelText
    .trim()
    .split(/\s+/u)
    .filter((part) => part.length > 0);

  if (normalized.length === 0) {
    return "DOC";
  }

  const joined = normalized
    .slice(0, 2)
    .map((part) => part.slice(0, 2))
    .join("")
    .toUpperCase();

  return joined.slice(0, 3) || "DOC";
}

function parseWorkbenchDateTimeText(valueText: string): Date | null {
  const trimmed = valueText.trim();

  if (trimmed.length === 0) {
    return null;
  }

  const isoMatch = trimmed.match(
    /\b(20\d{2})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(Z|[+-]\d{2}:?\d{2}))?)?\b/u,
  );

  if (isoMatch) {
    const [, year, month, day, hour = "12", minute = "00", second = "00", zone] =
      isoMatch;
    const dateText = zone
      ? `${year}-${month}-${day}T${hour.padStart(2, "0")}:${minute}:${second}${zone}`
      : `${year}-${month}-${day}T${hour.padStart(2, "0")}:${minute}:${second}Z`;
    const parsed = new Date(dateText);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const usMatch = trimmed.match(/\b(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/(20\d{2})\b/u);

  if (!usMatch) {
    return null;
  }

  const [, rawMonth, rawDay, rawYear] = usMatch;
  const afterDateText = trimmed.slice((usMatch.index ?? 0) + usMatch[0].length);
  const timeMatch = afterDateText.match(
    /(?:\btime\s*:\s*)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\b/iu,
  );
  let hour = timeMatch ? Number.parseInt(timeMatch[1] ?? "12", 10) : 12;
  const minute = timeMatch ? Number.parseInt(timeMatch[2] ?? "0", 10) : 0;
  const meridiem = timeMatch?.[3]?.toLowerCase();

  if (meridiem === "pm" && hour < 12) {
    hour += 12;
  } else if (meridiem === "am" && hour === 12) {
    hour = 0;
  }

  const parsed = new Date(
    Number.parseInt(rawYear, 10),
    Number.parseInt(rawMonth, 10) - 1,
    Number.parseInt(rawDay, 10),
    hour,
    Number.isFinite(minute) ? minute : 0,
  );

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatLegacyPreviewDatePrimaryText(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  })
    .format(date)
    .toUpperCase();
}

function formatLegacyPreviewDateSecondaryText(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    hour12: true,
    minute: "2-digit",
  })
    .format(date)
    .toUpperCase();
}
export type PatientWorkspaceSummarySurfaceRow = {
  id: string;
  title: string;
  subtitle?: string | null;
  supportingText?: string | null;
  badgeText?: string | null;
  leadPrimaryText?: string | null;
  leadSecondaryText?: string | null;
  tone: "neutral" | "accent" | "warning" | "info";
};

export type PatientDocumentDateRangeFilter = "all" | "last-30-days" | "last-90-days";
export type PatientDocumentTypeFilter =
  | "all"
  | "consents"
  | "questionnaires"
  | "lab-results"
  | "post-procedure-notes"
  | "other";
export type PatientDocumentAvailabilityFilter =
  | "all"
  | "download-ready"
  | "metadata-only";

export type PatientWorkspaceActionableListItem = PatientWorkspaceSummarySurfaceRow & {
  detailSummaryText: string;
  detailTitle: string;
  detailRows: ReadonlyArray<{
    id: string;
    labelText: string;
    valueText: string;
  }>;
  primaryActionLabel: string;
  primaryActionNotice: string;
  secondaryActionLabel?: string | null;
  secondaryActionNotice?: string | null;
};

type PatientWorkspaceDetailSectionId =
  PatientWorkspaceSummaryResponseV1["patient"]["detailSections"][number]["id"];
type PatientWorkspaceDetailRow =
  PatientWorkspaceSummaryResponseV1["patient"]["detailSections"][number]["rows"][number];
type PatientWorkspaceActionableDetailRow =
  PatientWorkspaceActionableListItem["detailRows"][number];
type PatientWorkspaceClinicalNote =
  PatientWorkspaceSummaryResponseV1["patient"]["clinicalNotes"][number];
type ParsedPatientWorkspaceSourceDetail = {
  dateText: string | null;
  descriptionText: string | null;
  locationText: string | null;
  parts: string[];
  physicianText: string | null;
  statusText: string | null;
};
type PatientWorkspaceRecordState = {
  bucket: number;
  isHistorical: boolean;
  labelText: string;
  sortTime: number | null;
};

export function resolvePatientWorkspaceProductText(valueText: string): string {
  const legacyConnectVendorPattern = new RegExp(
    `\\b${["Amazon", "Connect"].join("\\s+")}\\b`,
    "giu",
  );
  const normalizedText = valueText
    .replace(
      /\bR1CR1-\d+\s+verification note created through the live clinician workbench action API;\s*patient-visible for propagation checks\.?/giu,
      "Clinical note available for patient care context.",
    )
    .replace(
      /\bR1CR1-\d+\s+hosted lifecycle note\s+[0-9T:.\-Z+]+/giu,
      "Clinical review note",
    )
    .replace(/\bR1CR1-\d+\s+QA medication edited\b/giu, "Medication update")
    .replace(/\bR1CR1-\d+\s+hosted appointment edited\b/giu, "Updated appointment")
    .replace(
      /\bR1CR1-\d+\s+patient document propagation note\b/giu,
      "Patient document update",
    )
    .replace(/\bQA medication edited\b/giu, "Medication update")
    .replace(/\bhosted appointment edited\b/giu, "Updated appointment")
    .replace(/\bpatient document propagation note\b/giu, "Patient document update")
    .replace(
      /\bPatient document update\s+created\s+[0-9T:.\-Z+]+;\s*this should be visible from Patient Documents and remain hidden only when archived\.?/giu,
      "Patient document update available for review.",
    )
    .replace(
      /\bcreated\s+[0-9T:.\-Z+]+;\s*this should be visible from Patient Documents and remain hidden only when archived\.?/giu,
      "available for review.",
    )
    .replace(/\bCloudFront verification\b/giu, "Care team review")
    .replace(/\bVerification record\b/giu, "Care team update")
    .replace(/\bhosted lifecycle note\b/giu, "Clinical review note")
    .replace(/\bhosted verification\b/giu, "care-team review")
    .replace(/\bverification note\b/giu, "clinical note")
    .replace(/\blive clinician workbench action API\b/giu, "clinical workspace")
    .replace(/\bpropagation checks?\b/giu, "patient-facing review")
    .replace(/\bsource documents\b/giu, "clinical documents")
    .replace(/\bsource document\b/giu, "clinical document")
    .replace(/\bprompt-ready\s+text\b/giu, "review-ready text")
    .replace(/\bprompt-ready\b/giu, "ready for review")
    .replace(legacyConnectVendorPattern, "AmpleCare Connect")
    .replace(/\bAmpleCare CR1 backfill\b/giu, "AmpleCare clinical summary")
    .replace(/\bdemo agent\b/giu, "care team")
    .replace(/^\s*JSON:\s*/iu, "")
    .replace(/\bR1CR1-\d+\b/giu, "Care record");
  const stillLooksInternal =
    /\bcodex\b/iu.test(normalizedText) ||
    /\bcloudfront\b/iu.test(normalizedText) ||
    /\bdb-only\b/iu.test(normalizedText) ||
    /\bsmoke\s+\d{4}/iu.test(normalizedText) ||
    /\bsmoke\/probe\b/iu.test(normalizedText) ||
    /\baction API\b/iu.test(normalizedText) ||
    /^\s*json\s*:/iu.test(normalizedText);

  if (stillLooksInternal) {
    return "Care-team update available.";
  }

  return normalizedText;
}

function resolvePatientWorkspaceProductTextOrNull(
  valueText: string | null,
): string | null {
  return valueText == null ? null : resolvePatientWorkspaceProductText(valueText);
}

function sanitizePatientWorkspaceClinicalNote(
  note: PatientWorkspaceClinicalNote,
): PatientWorkspaceClinicalNote {
  return {
    ...note,
    authoredByLabel: resolvePatientWorkspaceProductText(note.authoredByLabel),
    bodyText: resolvePatientWorkspaceProductText(note.bodyText),
    sourceLabel: resolvePatientWorkspaceProductTextOrNull(note.sourceLabel),
    title: resolvePatientWorkspaceProductText(note.title),
  };
}

function sanitizePatientWorkspaceClinicalNotes(
  notes: PatientWorkspaceClinicalNote[] | undefined,
): PatientWorkspaceClinicalNote[] | undefined {
  return notes?.map(sanitizePatientWorkspaceClinicalNote);
}

export function sanitizePatientWorkspaceSummaryForProductDisplay(
  summary: PatientWorkspaceSummaryResponseV1,
): PatientWorkspaceSummaryResponseV1 {
  const patient = summary.patient;

  return {
    ...summary,
    patient: {
      ...patient,
      clinicalNotes: patient.clinicalNotes.map(sanitizePatientWorkspaceClinicalNote),
      contactWindowText: resolvePatientWorkspaceProductText(patient.contactWindowText),
      conversation: {
        ...patient.conversation,
        messages: patient.conversation.messages.map((message) => ({
          ...message,
          bodyText: resolvePatientWorkspaceProductText(message.bodyText),
          review: message.review
            ? {
                ...message.review,
                clinicianNotes: resolvePatientWorkspaceProductTextOrNull(
                  message.review.clinicianNotes,
                ),
              }
            : null,
          senderDisplayName: resolvePatientWorkspaceProductText(
            message.senderDisplayName,
          ),
        })),
      },
      coordinatorLabel: resolvePatientWorkspaceProductText(patient.coordinatorLabel),
      deliverySummaries: patient.deliverySummaries.map((delivery) => ({
        ...delivery,
        channelLabel: resolvePatientWorkspaceProductText(delivery.channelLabel),
        recipientLabel: resolvePatientWorkspaceProductText(delivery.recipientLabel),
        statusLabel: resolvePatientWorkspaceProductText(delivery.statusLabel),
      })),
      detailSections: patient.detailSections.map((section) => ({
        ...section,
        notes: sanitizePatientWorkspaceClinicalNotes(section.notes),
        rows: section.rows.map((row) => ({
          ...row,
          label: resolvePatientWorkspaceProductText(row.label),
          notes: sanitizePatientWorkspaceClinicalNotes(row.notes),
          valueText: resolvePatientWorkspaceProductText(row.valueText),
        })),
        summaryText: resolvePatientWorkspaceProductTextOrNull(section.summaryText),
        title: resolvePatientWorkspaceProductText(section.title),
      })),
      documentSummaries: patient.documentSummaries.map((document) => ({
        ...document,
        createdByLabel: resolvePatientWorkspaceProductText(document.createdByLabel),
        label: resolvePatientWorkspaceProductText(document.label),
        notes: sanitizePatientWorkspaceClinicalNotes(document.notes),
        statusLabel: resolvePatientWorkspaceProductText(document.statusLabel),
        summaryText: resolvePatientWorkspaceProductText(document.summaryText),
      })),
      medicationSummaryText: resolvePatientWorkspaceProductText(
        patient.medicationSummaryText,
      ),
      nextAppointmentText: resolvePatientWorkspaceProductText(
        patient.nextAppointmentText,
      ),
      procedureSummaryText: resolvePatientWorkspaceProductText(
        patient.procedureSummaryText,
      ),
      programLabel: resolvePatientWorkspaceProductText(patient.programLabel),
      statusLabel: resolvePatientWorkspaceProductText(patient.statusLabel),
      summaryText: resolvePatientWorkspaceProductText(patient.summaryText),
      taskAssignments: patient.taskAssignments.map((assignment) => ({
        ...assignment,
        statusLabel: resolvePatientWorkspaceProductText(assignment.statusLabel),
        targetLabel: resolvePatientWorkspaceProductText(assignment.targetLabel),
        taskLabel: resolvePatientWorkspaceProductText(assignment.taskLabel),
      })),
      voiceCallSummaries: patient.voiceCallSummaries.map((voiceCall) => ({
        ...voiceCall,
        previewText: resolvePatientWorkspaceProductText(voiceCall.previewText),
        questionClass: resolvePatientWorkspaceProductTextOrNull(
          voiceCall.questionClass,
        ),
        sourceLabel: resolvePatientWorkspaceProductTextOrNull(
          voiceCall.sourceLabel,
        ),
        transcriptTurns: voiceCall.transcriptTurns.map((turn) => ({
          ...turn,
          bodyText: resolvePatientWorkspaceProductText(turn.bodyText),
          speakerLabel: resolvePatientWorkspaceProductText(turn.speakerLabel),
        })),
      })),
    },
  };
}

function readPatientWorkspaceDetailRows(
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null,
  sectionId: PatientWorkspaceDetailSectionId,
): PatientWorkspaceDetailRow[] {
  return (
    summary?.detailSections
      .find((section) => section.id === sectionId)
      ?.rows.filter(
        (row) => row.label.trim().length > 0 || row.valueText.trim().length > 0,
      ) ?? []
  );
}

function readPatientWorkspaceSourceParts(valueText: string): string[] {
  return valueText
    .split(/[|\n]+/u)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function readPatientWorkspaceLabeledSourcePart(
  part: string,
): { labelText: string; valueText: string } | null {
  const match = part.match(/^([^:]{2,64}):\s*(.+)$/u);

  if (!match) {
    return null;
  }

  const [, rawLabel = "", rawValue = ""] = match;
  const labelText = rawLabel.trim();
  const valueText = rawValue.trim();

  return labelText && valueText ? { labelText, valueText } : null;
}

function appendPatientWorkspaceSourceDetailRows(
  rows: PatientWorkspaceActionableDetailRow[],
  input: {
    excludedLabels: readonly string[];
    parts: readonly string[];
    rowId: string;
  },
): void {
  const excludedLabels = new Set(
    input.excludedLabels.map((label) => label.trim().toLowerCase()),
  );

  input.parts.forEach((part, index) => {
    const labeledPart = readPatientWorkspaceLabeledSourcePart(part);

    if (!labeledPart) {
      return;
    }

    if (excludedLabels.has(labeledPart.labelText.toLowerCase())) {
      return;
    }

    appendPatientWorkspaceDetailRow(rows, {
      id: `${input.rowId}-source-${index}`,
      labelText: labeledPart.labelText,
      valueText: labeledPart.valueText,
    });
  });
}

function readPatientWorkspaceSourcePart(
  parts: string[],
  label: string,
): string | null {
  const prefix = `${label}:`;
  const value =
    parts
      .find((part) => part.toLowerCase().startsWith(prefix.toLowerCase()))
      ?.slice(prefix.length)
      .trim() ?? "";

  return value || null;
}

function formatPatientWorkspaceSourceDateTime(
  parsedDate: Date | null,
  fallbackText: string | null,
): string | null {
  if (parsedDate == null) {
    return fallbackText;
  }

  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    hour: "numeric",
    hour12: true,
    minute: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsedDate);
}

function parsePatientWorkspaceSourceDetail(
  valueText: string,
): ParsedPatientWorkspaceSourceDetail {
  const parts = readPatientWorkspaceSourceParts(valueText);
  const datePart = parts.find((part) => parseWorkbenchDateTimeText(part));
  const parsedDate = datePart ? parseWorkbenchDateTimeText(datePart) : null;
  const prefixedLabels = [
    "status:",
    "location:",
    "surgeon:",
    "provider:",
    "service:",
    "room:",
    "facility:",
    "complete:",
    "cancel reason:",
  ];
  const descriptionText =
    parts.find((part) => {
      if (parseWorkbenchDateTimeText(part)) {
        return false;
      }

      return !prefixedLabels.some((prefix) =>
        part.toLowerCase().startsWith(prefix),
      );
    }) ?? null;
  const physicianText =
    readPatientWorkspaceSourcePart(parts, "Surgeon") ??
    readPatientWorkspaceSourcePart(parts, "Provider");

  return {
    dateText: formatPatientWorkspaceSourceDateTime(parsedDate, datePart ?? null),
    descriptionText,
    locationText:
      readPatientWorkspaceSourcePart(parts, "Location") ??
      readPatientWorkspaceSourcePart(parts, "Facility") ??
      readPatientWorkspaceSourcePart(parts, "Room"),
    parts,
    physicianText,
    statusText: readPatientWorkspaceSourcePart(parts, "Status"),
  };
}

function resolvePatientWorkspaceRecordState(
  row: PatientWorkspaceDetailRow,
  sectionId: PatientWorkspaceDetailSectionId,
): PatientWorkspaceRecordState {
  const sourceParts = readPatientWorkspaceSourceParts(row.valueText);
  const sourceStatusText =
    readPatientWorkspaceSourcePart(sourceParts, "Status")?.toLowerCase() ?? "";
  const clinicalStatusText = row.clinicalStatus ?? "active";
  const isArchived =
    clinicalStatusText === "archived" ||
    ["archived", "inactive", "discontinued", "cancelled", "canceled"].some(
      (status) => sourceStatusText.includes(status),
    );
  const datePart = sourceParts.find((part) => parseWorkbenchDateTimeText(part));
  const parsedDate = datePart ? parseWorkbenchDateTimeText(datePart) : null;
  const sortTime = parsedDate?.getTime() ?? null;
  const isDatedClinicalRecord =
    sectionId === "appointments" || sectionId === "procedures";
  const isPastDatedRecord =
    isDatedClinicalRecord && sortTime != null && sortTime < Date.now();

  if (isArchived) {
    return {
      bucket: 3,
      isHistorical: true,
      labelText: "Archived",
      sortTime,
    };
  }

  if (isPastDatedRecord) {
    return {
      bucket: 2,
      isHistorical: true,
      labelText: "Past",
      sortTime,
    };
  }

  if (isDatedClinicalRecord && sortTime != null) {
    return {
      bucket: 0,
      isHistorical: false,
      labelText: "Upcoming",
      sortTime,
    };
  }

  return {
    bucket: 1,
    isHistorical: false,
    labelText: sectionId === "medications" ? "Current" : "Active",
    sortTime,
  };
}

function sortPatientWorkspaceDetailRowsForPatientSurface(
  rows: PatientWorkspaceDetailRow[],
  sectionId: PatientWorkspaceDetailSectionId,
): PatientWorkspaceDetailRow[] {
  return rows
    .map((row, index) => ({
      index,
      row,
      state: resolvePatientWorkspaceRecordState(row, sectionId),
    }))
    .sort((left, right) => {
      if (left.state.bucket !== right.state.bucket) {
        return left.state.bucket - right.state.bucket;
      }

      if (left.state.sortTime != null && right.state.sortTime != null) {
        return left.state.sortTime - right.state.sortTime;
      }

      return left.index - right.index;
    })
    .map(({ row }) => row);
}

function appendPatientWorkspaceDetailRow(
  rows: PatientWorkspaceActionableDetailRow[],
  row: {
    id: string;
    labelText: string;
    valueText: string | null | undefined;
  },
): void {
  const valueText = row.valueText?.trim() ?? "";

  if (valueText.length === 0) {
    return;
  }

  rows.push({
    id: row.id,
    labelText: row.labelText,
    valueText,
  });
}

function appendPatientWorkspaceClinicalNoteRows(
  rows: PatientWorkspaceActionableDetailRow[],
  row: PatientWorkspaceDetailRow,
): void {
  for (const note of row.notes ?? []) {
    appendPatientWorkspaceDetailRow(rows, {
      id: `${row.id}-${note.noteId}`,
      labelText: note.title || "Clinical note",
      valueText: note.bodyText,
    });
  }
}

function buildPatientWorkspaceStructuredDetailRows(
  input: {
    detailTitle: string;
    row: PatientWorkspaceDetailRow;
    sectionId: PatientWorkspaceDetailSectionId;
    summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  },
): PatientWorkspaceActionableListItem["detailRows"] | null {
  if (
    input.sectionId !== "appointments" &&
    input.sectionId !== "procedures" &&
    input.sectionId !== "medications"
  ) {
    return null;
  }

  const parsed = parsePatientWorkspaceSourceDetail(input.row.valueText);
  const descriptionText =
    parsed.descriptionText ?? input.row.label.trim() ?? input.detailTitle;
  const rows: PatientWorkspaceActionableDetailRow[] = [];
  const isMedication = input.sectionId === "medications";

  appendPatientWorkspaceDetailRow(rows, {
    id: `${input.row.id}-description`,
    labelText: isMedication
      ? "Medication"
      : input.sectionId === "appointments"
        ? "Appointment"
        : "Procedure",
    valueText: descriptionText,
  });

  if (!isMedication) {
    appendPatientWorkspaceDetailRow(rows, {
      id: `${input.row.id}-date-time`,
      labelText: "Date and time",
      valueText: parsed.dateText,
    });
    appendPatientWorkspaceDetailRow(rows, {
      id: `${input.row.id}-location`,
      labelText: "Location",
      valueText: parsed.locationText,
    });
    appendPatientWorkspaceDetailRow(rows, {
      id: `${input.row.id}-status`,
      labelText: "Status",
      valueText: parsed.statusText,
    });
  }

  appendPatientWorkspaceSourceDetailRows(rows, {
    excludedLabels: isMedication
      ? []
      : ["date", "time", "location", "facility", "room", "status"],
    parts: parsed.parts,
    rowId: input.row.id,
  });
  appendPatientWorkspaceDetailRow(rows, {
    id: `${input.row.id}-record-state`,
    labelText: "Record state",
    valueText: resolvePatientWorkspaceRecordState(
      input.row,
      input.sectionId,
    ).labelText,
  });
  appendPatientWorkspaceDetailRow(rows, {
    id: `${input.row.id}-clinician`,
    labelText: isMedication
      ? "Care team"
      : input.sectionId === "appointments"
        ? "Receiving clinician"
        : "Leading clinician",
    valueText: parsed.physicianText ?? input.summary?.coordinatorLabel,
  });
  appendPatientWorkspaceClinicalNoteRows(rows, input.row);

  return rows.length > 0 ? rows : null;
}

function buildPatientWorkspaceFallbackDetailRows(
  row: PatientWorkspaceDetailRow,
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null,
): PatientWorkspaceActionableListItem["detailRows"] {
  const rows: PatientWorkspaceActionableDetailRow[] = [
    {
      id: `${row.id}-source`,
      labelText: row.label.trim() || "Source detail",
      valueText: row.valueText.trim() || "No detail value recorded",
    },
    ...(summary?.programLabel?.trim()
      ? [
          {
            id: `${row.id}-program`,
            labelText: "Program",
            valueText: summary.programLabel,
          },
        ]
      : []),
    ...(summary?.coordinatorLabel?.trim()
      ? [
          {
            id: `${row.id}-coordinator`,
            labelText: "Care team",
            valueText: summary.coordinatorLabel,
          },
        ]
      : []),
    ...(summary?.contactWindowText?.trim()
      ? [
          {
            id: `${row.id}-contact-window`,
            labelText: "Contact window",
            valueText: summary.contactWindowText,
          },
        ]
      : []),
  ];

  appendPatientWorkspaceClinicalNoteRows(rows, row);

  return rows;
}

function buildPatientWorkspaceSourceItems(input: {
  detailSummaryText: string;
  detailTitle: string;
  itemIdPrefix: string;
  primaryActionLabel: string;
  primaryActionNotice: string;
  secondaryActionLabel?: string;
  secondaryActionNotice?: string;
  sectionId: PatientWorkspaceDetailSectionId;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  tone: PatientWorkspaceSummarySurfaceRow["tone"];
}): PatientWorkspaceActionableListItem[] {
  const rows = sortPatientWorkspaceDetailRowsForPatientSurface(
    readPatientWorkspaceDetailRows(input.summary, input.sectionId),
    input.sectionId,
  );

  return rows.map((row, index) => {
    const labelText = row.label.trim() || `${input.detailTitle} ${index + 1}`;
    const valueText = row.valueText.trim();
    const parsedDate = parseWorkbenchDateTimeText(valueText);
    const parsedSourceDetail = parsePatientWorkspaceSourceDetail(valueText);
    const recordState = resolvePatientWorkspaceRecordState(
      row,
      input.sectionId,
    );
    const displayTitle = parsedSourceDetail.descriptionText ?? labelText;
    const displaySubtitle = [
      recordState.labelText,
      parsedSourceDetail.dateText,
      parsedSourceDetail.locationText,
      parsedSourceDetail.statusText ? `Status: ${parsedSourceDetail.statusText}` : null,
    ]
      .filter((part): part is string => part != null && part.length > 0)
      .join(" - ");

    return {
      id: `${input.itemIdPrefix}-${row.id || index}`,
      title: displayTitle,
      subtitle: displaySubtitle || valueText || null,
      supportingText:
        recordState.isHistorical
          ? "Historical source record"
          : input.summary?.coordinatorLabel?.trim() ||
        input.summary?.summaryText ||
        null,
      leadPrimaryText: parsedDate
        ? formatLegacyPreviewDatePrimaryText(parsedDate)
        : recordState.isHistorical
          ? recordState.labelText.toUpperCase()
          : resolveLegacyPreviewFallbackPrimaryText(labelText),
      leadSecondaryText: parsedDate
        ? formatLegacyPreviewDateSecondaryText(parsedDate)
        : recordState.labelText.toLowerCase() ||
          resolveLegacyPreviewFallbackSecondaryText(labelText),
      tone: input.tone,
      detailTitle: input.detailTitle,
      detailSummaryText: input.detailSummaryText,
      detailRows:
        buildPatientWorkspaceStructuredDetailRows({
          detailTitle: input.detailTitle,
          row,
          sectionId: input.sectionId,
          summary: input.summary,
        }) ?? buildPatientWorkspaceFallbackDetailRows(row, input.summary),
      primaryActionLabel: input.primaryActionLabel,
      primaryActionNotice: input.primaryActionNotice,
      secondaryActionLabel: input.secondaryActionLabel ?? null,
      secondaryActionNotice: input.secondaryActionNotice ?? null,
    };
  });
}

export function buildPatientWorkspaceUpdateRows(
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null,
): PatientWorkspaceSummarySurfaceRow[] {
  return [
    ...(summary?.taskAssignments ?? []).map((assignment) => ({
      id: `task-${assignment.assignmentId}`,
      title: assignment.taskLabel,
      subtitle: `${assignment.targetLabel} | ${assignment.statusLabel}`,
      leadPrimaryText: "TASK",
      leadSecondaryText: "queue",
      tone: "warning" as const,
    })),
    ...(summary?.deliverySummaries ?? []).map((delivery) => ({
      id: `delivery-${delivery.deliveryId}`,
      title: delivery.channelLabel,
      subtitle: `${delivery.recipientLabel} | ${delivery.statusLabel}`,
      supportingText: delivery.updatedAtText,
      leadPrimaryText: "SEND",
      leadSecondaryText: "trace",
      tone: "info" as const,
    })),
  ];
}

export function buildPatientWorkspaceDocumentRows(
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null,
  limit?: number,
): PatientWorkspaceSummarySurfaceRow[] {
  const documents = limit
    ? (summary?.documentSummaries ?? []).slice(0, limit)
    : (summary?.documentSummaries ?? []);

  return documents.map((document) => ({
    id: document.documentId,
    title: document.label,
    subtitle: [
      document.statusLabel,
      document.createdByLabel,
      document.createdAt.slice(0, 10),
    ].join(" | "),
    supportingText:
      document.notes?.[0]?.bodyText ?? document.summaryText,
    badgeText: resolveDocumentBadgeText(document.label),
    leadPrimaryText: resolveLegacyCompactLeadText(
      resolveDocumentBadgeText(document.label),
      "DOC",
    ),
    leadSecondaryText: document.createdAt.slice(5, 10),
    tone: document.availability === "download-ready" ? "accent" : "neutral",
  }));
}

export function resolvePatientDocumentType(
  labelText: string,
): PatientDocumentTypeFilter {
  const normalized = labelText.trim().toLowerCase();

  if (normalized.includes("consent")) {
    return "consents";
  }

  if (
    normalized.includes("questionnaire") ||
    normalized.includes("screening") ||
    normalized.includes("asb")
  ) {
    return "questionnaires";
  }

  if (
    normalized.includes("lab") ||
    normalized.includes("pathology") ||
    normalized.includes("mri") ||
    normalized.includes("report")
  ) {
    return "lab-results";
  }

  if (
    normalized.includes("post-procedure") ||
    normalized.includes("post procedure") ||
    normalized.includes("procedure note")
  ) {
    return "post-procedure-notes";
  }

  return "other";
}

function matchesPatientDocumentDateRange(
  createdAt: string,
  filter: PatientDocumentDateRangeFilter,
): boolean {
  if (filter === "all") {
    return true;
  }

  const createdAtDate = new Date(createdAt);

  if (Number.isNaN(createdAtDate.getTime())) {
    return true;
  }

  const days =
    filter === "last-30-days" ? 30 : filter === "last-90-days" ? 90 : 3650;
  const oldestIncludedDate = Date.now() - days * 24 * 60 * 60 * 1000;

  return createdAtDate.getTime() >= oldestIncludedDate;
}

export function filterPatientWorkspaceDocuments(input: {
  availabilityFilter: PatientDocumentAvailabilityFilter;
  dateRangeFilter: PatientDocumentDateRangeFilter;
  searchQuery: string;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  typeFilter: PatientDocumentTypeFilter;
}) {
  const {
    availabilityFilter,
    dateRangeFilter,
    searchQuery,
    summary,
    typeFilter,
  } = input;
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  return [...(summary?.documentSummaries ?? [])]
    .filter((document) =>
      availabilityFilter === "all"
        ? true
        : document.availability === availabilityFilter,
    )
    .filter((document) =>
      typeFilter === "all"
        ? true
        : resolvePatientDocumentType(document.label) === typeFilter,
    )
    .filter((document) =>
      matchesPatientDocumentDateRange(document.createdAt, dateRangeFilter),
    )
    .filter((document) => {
      if (normalizedSearchQuery.length === 0) {
        return true;
      }

      return [
        document.label,
        document.summaryText,
        document.statusLabel,
        document.createdByLabel,
        ...(document.notes ?? []).map((note) => note.bodyText),
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearchQuery);
    })
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
}

export function buildPatientWorkspaceAppointmentItems(
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null,
  updateRows: PatientWorkspaceSummarySurfaceRow[],
): PatientWorkspaceActionableListItem[] {
  const sourceAppointmentItems = buildPatientWorkspaceSourceItems({
    detailSummaryText:
      "Appointment details imported from the connected source schedule.",
    detailTitle: "Appointment detail",
    itemIdPrefix: "appointment-source",
    primaryActionLabel: "Open details",
    primaryActionNotice:
      "Opened appointment details from the connected source schedule.",
    secondaryActionLabel: "Message care team",
    secondaryActionNotice:
      "Care-team message opened from the appointment detail panel.",
    sectionId: "appointments",
    summary,
    tone: "info",
  });

  if (sourceAppointmentItems.length > 0) {
    return sourceAppointmentItems;
  }

  return [
    {
      id: "appointment-visit",
      title: "Upcoming visit",
      subtitle: summary?.nextAppointmentText ?? "Not scheduled yet",
      supportingText: summary?.coordinatorLabel?.trim().length
        ? `Coordinator ${summary.coordinatorLabel}`
        : (summary?.summaryText ?? null),
      leadPrimaryText: "NEXT",
      leadSecondaryText: "visit",
      tone: "info",
      detailTitle: "Visit details",
      detailSummaryText:
        "Appointment details, paperwork, and reminders are grouped here.",
      detailRows: [
        {
          id: "appointment-time",
          labelText: "Date and time",
          valueText: summary?.nextAppointmentText ?? "Not scheduled yet",
        },
        {
          id: "appointment-coordinator",
          labelText: "Receiving clinician",
          valueText: summary?.coordinatorLabel ?? "Not assigned",
        },
      ],
      primaryActionLabel: "Confirm attendance",
      primaryActionNotice:
        "Attendance confirmation recorded from the patient appointment path.",
      secondaryActionLabel: "Request callback",
      secondaryActionNotice:
        "Callback request queued from the appointment detail panel.",
    },
    {
      id: "appointment-paperwork",
      title: "Paperwork and records",
      subtitle:
        (summary?.pendingDocumentCount ?? 0) > 0
          ? `${summary?.pendingDocumentCount ?? 0} documents pending`
          : "No pending documents",
      supportingText:
        (summary?.documentSummaries.length ?? 0) > 0
          ? `${summary?.documentSummaries.length ?? 0} records already in the workspace`
          : "Bring records to check-in if requested",
      leadPrimaryText: "DOC",
      leadSecondaryText: "forms",
      tone: "neutral",
      detailTitle: "Paperwork checklist",
      detailSummaryText:
        "Paperwork and records needed before the appointment.",
      detailRows: [
        {
          id: "appointment-documents-pending",
          labelText: "Pending documents",
          valueText: String(summary?.pendingDocumentCount ?? 0),
        },
        {
          id: "appointment-documents-available",
          labelText: "Records available",
          valueText: String(summary?.documentSummaries.length ?? 0),
        },
        {
          id: "appointment-unread",
          labelText: "Unread messages",
          valueText: String(summary?.unreadMessageCount ?? 0),
        },
      ],
      primaryActionLabel: "Open documents",
      primaryActionNotice:
        "Document workspace opened from the appointment checklist flow.",
      secondaryActionLabel: "Message care team",
      secondaryActionNotice:
        "Care-team message opened from the appointment paperwork panel.",
    },
    {
      id: "appointment-reminders",
      title: "Preparation reminders",
      subtitle:
        updateRows.length > 0
          ? `${updateRows.length} active reminders`
          : "No preparation reminders",
      supportingText: summary?.statusLabel?.trim().length
        ? `Current status ${summary.statusLabel}`
        : "Status unavailable",
      leadPrimaryText: "TASK",
      leadSecondaryText: "prep",
      tone: "warning",
      detailTitle: "Reminder actions",
      detailSummaryText:
        "Self-service reminder controls grouped around the next visit.",
      detailRows: [
        {
          id: "appointment-reminder-count",
          labelText: "Open reminder items",
          valueText: String(updateRows.length),
        },
        {
          id: "appointment-reminder-program",
          labelText: "Program",
          valueText: summary?.programLabel ?? "Not assigned",
        },
        {
          id: "appointment-reminder-contact",
          labelText: "Contact window",
          valueText: summary?.contactWindowText ?? "Not recorded yet",
        },
      ],
      primaryActionLabel: "Set reminder",
      primaryActionNotice:
        "Appointment reminder opened from the patient workspace.",
      secondaryActionLabel: "Launch agent",
      secondaryActionNotice:
        "Agent launch opened from the appointment reminder panel.",
    },
  ];
}

export function buildPatientWorkspaceProcedureItems(
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null,
  updateRows: PatientWorkspaceSummarySurfaceRow[],
): PatientWorkspaceActionableListItem[] {
  const sourceProcedureItems = buildPatientWorkspaceSourceItems({
    detailSummaryText:
      "Procedure details imported from the connected source procedure feed.",
    detailTitle: "Procedure detail",
    itemIdPrefix: "procedure-source",
    primaryActionLabel: "Open details",
    primaryActionNotice:
      "Opened procedure details from the connected source procedure feed.",
    secondaryActionLabel: "Ask coordinator",
    secondaryActionNotice:
      "Coordinator outreach requested from the procedure detail page.",
    sectionId: "procedures",
    summary,
    tone: "warning",
  });

  if (sourceProcedureItems.length > 0) {
    return sourceProcedureItems;
  }

  return [
    {
      id: "procedure-plan",
      title: "Procedure plan",
      subtitle: summary?.procedureSummaryText ?? "No procedure on file",
      supportingText: summary?.statusLabel?.trim().length
        ? `Current status ${summary.statusLabel}`
        : (summary?.summaryText ?? null),
      leadPrimaryText: "PROC",
      leadSecondaryText: "plan",
      tone: "warning",
      detailTitle: "Procedure detail",
      detailSummaryText:
        "Procedure details and care-team contacts.",
      detailRows: [
        {
          id: "procedure-detail-main",
          labelText: "Procedure",
          valueText: summary?.procedureSummaryText ?? "No procedure on file",
        },
        {
          id: "procedure-detail-coordinator",
          labelText: "Leading clinician",
          valueText: summary?.coordinatorLabel ?? "Not assigned",
        },
      ],
      primaryActionLabel: "Review prep checklist",
      primaryActionNotice:
        "Procedure preparation checklist opened from the patient workspace.",
      secondaryActionLabel: "Ask coordinator",
      secondaryActionNotice:
        "Coordinator outreach requested from the procedure detail page.",
    },
    {
      id: "procedure-checklist",
      title: "Day-before checklist",
      subtitle:
        updateRows.length > 0
          ? `${updateRows.length} updates in queue`
          : "Checklist not yet populated",
      supportingText:
        (summary?.pendingDocumentCount ?? 0) > 0
          ? `${summary?.pendingDocumentCount ?? 0} documents still pending`
          : "Document checklist clear",
      leadPrimaryText: "TASK",
      leadSecondaryText: "check",
      tone: "accent",
      detailTitle: "Checklist details",
      detailSummaryText:
        "Day-before and arrival tasks for the procedure.",
      detailRows: [
        {
          id: "procedure-checklist-updates",
          labelText: "Open updates",
          valueText: String(updateRows.length),
        },
        {
          id: "procedure-checklist-docs",
          labelText: "Pending documents",
          valueText: String(summary?.pendingDocumentCount ?? 0),
        },
        {
          id: "procedure-checklist-messages",
          labelText: "Unread messages",
          valueText: String(summary?.unreadMessageCount ?? 0),
        },
      ],
      primaryActionLabel: "View instructions",
      primaryActionNotice:
        "Procedure instructions opened from the checklist detail view.",
      secondaryActionLabel: "Open documents",
      secondaryActionNotice:
        "Document workspace opened from the procedure checklist view.",
    },
    {
      id: "procedure-recovery",
      title: "Recovery follow-up",
      subtitle: summary?.coordinatorLabel?.trim().length
        ? `Follow-up with ${summary.coordinatorLabel}`
        : "Follow-up not assigned",
      supportingText: summary?.medicationSummaryText?.trim().length
        ? summary.medicationSummaryText
        : "Medication follow-up note not available",
      leadPrimaryText: "CARE",
      leadSecondaryText: "after",
      tone: "info",
      detailTitle: "Recovery support",
      detailSummaryText:
        "Recovery and aftercare controls pushed into a dedicated procedure follow-up panel.",
      detailRows: [
        {
          id: "procedure-recovery-status",
          labelText: "Status",
          valueText: summary?.statusLabel ?? "Status unavailable",
        },
        {
          id: "procedure-recovery-program",
          labelText: "Program",
          valueText: summary?.programLabel ?? "Not assigned",
        },
        {
          id: "procedure-recovery-medication",
          labelText: "Medication note",
          valueText:
            summary?.medicationSummaryText ?? "No medication note on file",
        },
      ],
      primaryActionLabel: "Ask recovery question",
      primaryActionNotice:
        "Recovery question queued from the procedure follow-up panel.",
      secondaryActionLabel: "Launch agent",
      secondaryActionNotice:
        "Agent handoff opened from the procedure follow-up panel.",
    },
  ];
}

export function buildPatientWorkspaceOnboardingItems(
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null,
  updateRows: PatientWorkspaceSummarySurfaceRow[],
): PatientWorkspaceActionableListItem[] {
  return [
    {
      id: "onboarding-intake",
      title: "Intake and consent",
      subtitle:
        (summary?.pendingDocumentCount ?? 0) > 0
          ? "Forms still need review"
          : "Intake packet ready",
      supportingText: summary?.statusLabel?.trim().length
        ? `Current status ${summary.statusLabel}`
        : "Status not available",
      leadPrimaryText: "FORM",
      leadSecondaryText: "start",
      tone: "accent",
      detailTitle: "Intake checklist",
      detailSummaryText:
        "Onboarding checklist for intake paperwork, consent capture, and readiness review.",
      detailRows: [
        {
          id: "onboarding-intake-status",
          labelText: "Checklist status",
          valueText:
            (summary?.pendingDocumentCount ?? 0) > 0
              ? "Awaiting paperwork review"
              : "Ready for review",
        },
        {
          id: "onboarding-intake-program",
          labelText: "Program",
          valueText: summary?.programLabel ?? "Program not assigned",
        },
        {
          id: "onboarding-intake-contact",
          labelText: "Contact window",
          valueText: summary?.contactWindowText ?? "Not recorded yet",
        },
      ],
      primaryActionLabel: "Start intake",
      primaryActionNotice:
        "Intake form flow opened from the patient onboarding workspace.",
      secondaryActionLabel: "Review consent",
      secondaryActionNotice:
        "Consent review opened from the patient onboarding workspace.",
    },
    {
      id: "onboarding-records",
      title: "Insurance and records",
      subtitle:
        (summary?.documentSummaries.length ?? 0) > 0
          ? `${summary?.documentSummaries.length ?? 0} records available`
          : "Records not available yet",
      supportingText:
        (summary?.pendingDocumentCount ?? 0) > 0
          ? `${summary?.pendingDocumentCount ?? 0} records still pending`
          : "No pending records",
      leadPrimaryText: "DOC",
      leadSecondaryText: "review",
      tone: "neutral",
      detailTitle: "Records workspace",
      detailSummaryText:
        "Review insurance, referral, and intake records already available to this workspace.",
      detailRows: [
        {
          id: "onboarding-records-available",
          labelText: "Records available",
          valueText: String(summary?.documentSummaries.length ?? 0),
        },
        {
          id: "onboarding-records-pending",
          labelText: "Pending records",
          valueText: String(summary?.pendingDocumentCount ?? 0),
        },
        {
          id: "onboarding-records-updates",
          labelText: "Queued reminders",
          valueText: String(updateRows.length),
        },
      ],
      primaryActionLabel: "Review records",
      primaryActionNotice:
        "Record review opened from the patient onboarding workspace.",
      secondaryActionLabel: "Open documents",
      secondaryActionNotice:
        "Document workspace opened from the patient onboarding checklist.",
    },
    {
      id: "onboarding-medications",
      title: "Medication and allergies",
      subtitle:
        summary?.medicationSummaryText ?? "Medication history still needed",
      supportingText: summary?.coordinatorLabel?.trim().length
        ? `Medication review with ${summary.coordinatorLabel}`
        : "Coordinator not assigned yet",
      leadPrimaryText: "RX",
      leadSecondaryText: "review",
      tone: "info",
      detailTitle: "Medication review",
      detailSummaryText:
        "Medication, allergy, and pre-procedure review step grouped into the onboarding path.",
      detailRows: [
        {
          id: "onboarding-medications-note",
          labelText: "Medication note",
          valueText:
            summary?.medicationSummaryText ?? "No medication note on file",
        },
        {
          id: "onboarding-medications-coordinator",
          labelText: "Coordinator",
          valueText: summary?.coordinatorLabel ?? "Not assigned",
        },
        {
          id: "onboarding-medications-procedure",
          labelText: "Procedure",
          valueText: summary?.procedureSummaryText ?? "No procedure on file",
        },
      ],
      primaryActionLabel: "Review medications",
      primaryActionNotice:
        "Medication review opened from the patient onboarding workspace.",
      secondaryActionLabel: "Ask a question",
      secondaryActionNotice:
        "Care-team outreach requested from the onboarding medication step.",
    },
    {
      id: "onboarding-agent",
      title: "Agent and welcome call",
      subtitle:
        (summary?.unreadMessageCount ?? 0) > 0
          ? `${summary?.unreadMessageCount ?? 0} unread welcome messages`
          : "Welcome conversation ready",
      supportingText: summary?.nextAppointmentText?.trim().length
        ? `Next touchpoint ${summary.nextAppointmentText}`
        : "No touchpoint scheduled yet",
      leadPrimaryText: "AGT",
      leadSecondaryText: "setup",
      tone: "warning",
      detailTitle: "Welcome sequence",
      detailSummaryText:
        "Agent setup, welcome-message review, and first-touch scheduling in one onboarding lane.",
      detailRows: [
        {
          id: "onboarding-agent-unread",
          labelText: "Unread messages",
          valueText: String(summary?.unreadMessageCount ?? 0),
        },
        {
          id: "onboarding-agent-appointment",
          labelText: "Next touchpoint",
          valueText: summary?.nextAppointmentText ?? "Not scheduled yet",
        },
        {
          id: "onboarding-agent-contact",
          labelText: "Contact window",
          valueText: summary?.contactWindowText ?? "Not recorded yet",
        },
      ],
      primaryActionLabel: `Open ${brand.assistantName}`,
      primaryActionNotice:
        `${brand.assistantName} handoff opened from the onboarding welcome sequence.`,
      secondaryActionLabel: "Request welcome call",
      secondaryActionNotice:
        "Welcome call request queued from the onboarding workspace.",
    },
  ];
}

export function buildPatientMedicationWorkspaceRows(
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null,
  updateRows: PatientWorkspaceSummarySurfaceRow[],
) {
  const sourceMedicationRows = readPatientWorkspaceDetailRows(
    summary,
    "medications",
  );

  if (sourceMedicationRows.length > 0) {
    return sortPatientWorkspaceDetailRowsForPatientSurface(
      sourceMedicationRows,
      "medications",
    ).map((row, index) => {
      const recordState = resolvePatientWorkspaceRecordState(
        row,
        "medications",
      );

      return {
        id: `medication-source-${row.id || index}`,
        labelText: row.label.trim() || `Medication ${index + 1}`,
        routineText: recordState.isHistorical ? "History" : "Current",
        noteText: row.valueText.trim() || "No medication detail recorded",
        statusText: recordState.labelText,
        actionText: "Open details",
        noticeText:
          "Opened medication detail from the connected source medication feed.",
        isHistorical: recordState.isHistorical,
      };
    });
  }

  return [
    {
      id: "medication-primary",
      labelText: "Medication plan",
      routineText: "Daily",
      noteText: summary?.medicationSummaryText ?? "No medication note on file",
      statusText: summary?.statusLabel ?? "Status unavailable",
      actionText: "Set reminder",
      noticeText: "Medication reminder opened from the patient workspace.",
      isHistorical: false,
    },
    {
      id: "medication-follow-up",
      labelText: "Follow-up check-in",
      routineText: "As needed",
      noteText:
        updateRows[0]?.title ??
        (summary?.coordinatorLabel
          ? `Care coordinator ${summary.coordinatorLabel}`
          : "No follow-up queued"),
      statusText:
        updateRows.length > 0 ? "Pending reminder" : "No follow-up queued",
      actionText: "Mark taken",
      noticeText: "Medication adherence opened from the reminder action panel.",
      isHistorical: false,
    },
  ];
}
